/**
 * NDA Acceptance Component - CR-1403
 *
 * Component for suppliers to review and accept/decline NDA:
 * - Display NDA document preview/download
 * - Accept/Decline buttons with confirmation
 * - Shows NDA status and history
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import {
  Loader2,
  FileText,
  Download,
  CheckCircle,
  XCircle,
  Shield,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { rfqAPI } from '../../lib/api';
import type { RFQNDA, RFQDocument } from '../../lib/api';
import { downloadQueryAttachment } from './queryThreadAttachments';
import { useFormatters } from '../../contexts/PreferencesContext';

interface NDAAcceptanceProps {
  nda?: RFQNDA;
  document?: RFQDocument;
  /** True when the RFQ requires an NDA but the customer has no NDA record
   *  for this supplier — nothing can be accepted, so say so instead of
   *  offering buttons that cannot work. */
  recordMissing?: boolean;
  onAccept: () => void | Promise<void>;
  onDecline: () => void | Promise<void>;
  loading?: boolean;
}

export default function NDAAcceptance({ nda, document, recordMissing = false, onAccept, onDecline, loading: externalLoading }: NDAAcceptanceProps) {
  const [internalLoading, setInternalLoading] = useState(false);
  const { formatDate } = useFormatters();
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);

  const loading = externalLoading || internalLoading;
  // With no document attached there is nothing to download, so the
  // "downloaded and reviewed" confirmation is not asked for — otherwise the
  // supplier could never satisfy it and never accept.
  const hasDocument = !!document?.file;

  const getStatusBadge = () => {
    if (!nda) {
      return (
        <Badge variant="outline" className="border-yellow-500 text-yellow-600">
          <Clock className="h-3 w-3 mr-1" />
          Pending Review
        </Badge>
      );
    }
    switch (nda.status) {
      case 'pending':
        return (
          <Badge variant="outline" className="border-yellow-500 text-yellow-600">
            <Clock className="h-3 w-3 mr-1" />
            Pending Review
          </Badge>
        );
      case 'accepted':
        return (
          <Badge className="bg-green-500">
            <CheckCircle className="h-3 w-3 mr-1" />
            Accepted
          </Badge>
        );
      case 'declined':
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            Declined
          </Badge>
        );
      default:
        return null;
    }
  };

  const handleAccept = async () => {
    if (!acceptTerms || (hasDocument && !hasReviewed)) {
      toast.error(hasDocument
        ? 'Download and review the NDA, then confirm both boxes to accept.'
        : 'Tick the box to accept the NDA terms.');
      return;
    }

    try {
      setInternalLoading(true);
      if (nda) {
        await rfqAPI.acceptNDA(nda.id);
        toast.success('NDA accepted successfully');
      }
      setShowAcceptDialog(false);
      await onAccept();
    } catch (error) {
      console.error('Error accepting NDA:', error);
      toast.error('Failed to accept NDA');
    } finally {
      setInternalLoading(false);
    }
  };

  const handleDecline = async () => {
    try {
      setInternalLoading(true);
      if (nda) {
        await rfqAPI.declineNDA(nda.id);
        toast.success('NDA declined');
      }
      setShowDeclineDialog(false);
      await onDecline();
    } catch (error) {
      console.error('Error declining NDA:', error);
      toast.error('Failed to decline NDA');
    } finally {
      setInternalLoading(false);
    }
  };

  // The serialized `file` URL is not fetchable — private media locally, an
  // unsigned link the bucket refuses (AccessDenied) on S3 — so the file comes
  // through the authenticated download endpoint, like query attachments.
  const handleDownload = async () => {
    if (!document?.file) {
      toast.error('The customer has not attached an NDA document to this RFQ. You can still accept or decline the NDA terms below.');
      return;
    }
    try {
      await downloadQueryAttachment(document);
    } catch (error: unknown) {
      console.error('Error downloading NDA document:', error);
      toast.error('Could not download the NDA document. Please try again.');
    }
    setHasReviewed(true);
  };

  // The RFQ requires an NDA but no NDA record exists for this supplier
  // (the customer's send never created one). Nothing here can be accepted;
  // tell the supplier what to do rather than showing dead buttons.
  if (recordMissing) {
    return (
      <Card className="border-yellow-200 bg-yellow-50/50">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-yellow-600" />
            <CardTitle>Non-Disclosure Agreement Required</CardTitle>
          </div>
          <CardDescription>
            This RFQ requires an NDA before you can view or respond to it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-yellow-100 border border-yellow-200 rounded-lg p-4 flex gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-yellow-800">NDA not yet issued</p>
              <p className="text-yellow-700 mt-1">
                The customer has not issued the NDA for this RFQ yet, so there is
                nothing to accept. Contact the customer and ask them to attach the
                NDA and re-send the RFQ.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Already responded
  if (nda && nda.status !== 'pending') {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Non-Disclosure Agreement</CardTitle>
            </div>
            {getStatusBadge()}
          </div>
        </CardHeader>
        <CardContent>
          <div className="bg-muted/50 rounded-lg p-4">
            {nda.status === 'accepted' ? (
              <div className="flex items-center gap-3">
                <CheckCircle className="h-8 w-8 text-green-500" />
                <div>
                  <p className="font-medium">NDA Accepted</p>
                  <p className="text-sm text-muted-foreground">
                    You accepted this NDA on{' '}
                    {nda.responded_at
                      ? formatDate(nda.responded_at)
                      : 'N/A'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <XCircle className="h-8 w-8 text-red-500" />
                <div>
                  <p className="font-medium">NDA Declined</p>
                  <p className="text-sm text-muted-foreground">
                    You declined this NDA. You cannot submit a response to this RFQ.
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-yellow-200 bg-yellow-50/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-yellow-600" />
              <CardTitle>Non-Disclosure Agreement Required</CardTitle>
            </div>
            {getStatusBadge()}
          </div>
          <CardDescription>
            You must accept the NDA before viewing or responding to this RFQ
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* NDA Document */}
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-8 w-8 text-muted-foreground" />
                <div>
                  <p className="font-medium">
                    {hasDocument ? (document?.file_name || 'Non-Disclosure Agreement') : 'Non-Disclosure Agreement'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {hasDocument
                      ? `${(document?.file_type || 'PDF').toUpperCase()} Document`
                      : 'No document attached by the customer — accept or decline the NDA terms below.'}
                  </p>
                </div>
              </div>
              {hasDocument && (
                <Button variant="outline" onClick={handleDownload}>
                  <Download className="h-4 w-4 mr-2" />
                  Download & Review
                </Button>
              )}
            </div>
          </div>

          {/* Warning */}
          <div className="bg-yellow-100 border border-yellow-200 rounded-lg p-4 flex gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-yellow-800">Important Notice</p>
              <p className="text-yellow-700 mt-1">
                Please carefully review the Non-Disclosure Agreement before proceeding.
                By accepting, you agree to be bound by its terms. If you decline, you
                will not be able to submit a response to this RFQ.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => setShowDeclineDialog(true)}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Decline NDA
            </Button>
            <Button
              onClick={() => setShowAcceptDialog(true)}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Accept NDA
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Accept Dialog */}
      <Dialog open={showAcceptDialog} onOpenChange={setShowAcceptDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Accept Non-Disclosure Agreement</DialogTitle>
            <DialogDescription>
              Please confirm you have reviewed and agree to the NDA terms.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {hasDocument ? (
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="reviewed"
                  checked={hasReviewed}
                  onCheckedChange={(checked) => setHasReviewed(checked as boolean)}
                />
                <Label htmlFor="reviewed" className="text-sm leading-relaxed">
                  I confirm that I have downloaded and reviewed the Non-Disclosure
                  Agreement document
                </Label>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                No NDA document is attached to this RFQ. By accepting you agree to
                treat all information in this RFQ as confidential.
              </div>
            )}
            <div className="flex items-start space-x-3">
              <Checkbox
                id="accept"
                checked={acceptTerms}
                onCheckedChange={(checked) => setAcceptTerms(checked as boolean)}
              />
              <Label htmlFor="accept" className="text-sm leading-relaxed">
                I accept the terms and conditions of the Non-Disclosure Agreement
                and understand that I am legally bound by them
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAcceptDialog(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAccept}
              disabled={loading || !acceptTerms || (hasDocument && !hasReviewed)}
              className="bg-green-600 hover:bg-green-700"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Accepting...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Accept NDA
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline Dialog */}
      <Dialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline Non-Disclosure Agreement</DialogTitle>
            <DialogDescription>
              Are you sure you want to decline this NDA?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-red-800">Warning</p>
                <p className="text-red-700 mt-1">
                  If you decline this NDA, you will not be able to:
                </p>
                <ul className="list-disc list-inside text-red-700 mt-2 space-y-1">
                  <li>View the full RFQ details</li>
                  <li>Submit a response to this RFQ</li>
                  <li>Participate in this procurement process</li>
                </ul>
                <p className="text-red-700 mt-2">This action cannot be undone.</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeclineDialog(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDecline}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Declining...
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 mr-2" />
                  Decline NDA
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
